// ════════════════════════════════════════════════════════════════════
// AUDIT LOG HELPER
//
// Single entry point every component calls when something audit-able
// happens. Validates the action against a frozen vocabulary (typo
// safety) and fire-and-forgets the insert so the calling code path
// stays fast.
//
// USAGE
// ──────────────────────────────────────────────────────────────────
//   import { audit, AUDIT_ACTION } from './lib/auditLog';
//
//   audit({
//     ownerId,
//     actorId: currentUser.id,
//     actorName: currentUser.name,
//     targetType: 'clients',
//     targetId: client.id,
//     targetLabel: client.name,
//     action: AUDIT_ACTION.VIEW,
//     metadata: { from: 'health_clients_list' },
//   });
//
// SAFETY
// ──────────────────────────────────────────────────────────────────
// · Failures are logged to the console but never propagated. Audit
//   is best-effort; we don't want a flaky network to break user
//   actions.
// · `metadata` is sanitized — we strip any keys that look like
//   secrets (passwords, tokens, keys) before insert.
// · IP + UA are captured at write time. Geo is not (the helper runs
//   on every page so prompting for location would be hostile).
// ════════════════════════════════════════════════════════════════════

import { supabase } from './supabase';

// Frozen vocabulary. Add new entries here, NEVER inline-stringify
// at call sites — typos become silent gaps in the audit trail.
export const AUDIT_ACTION = Object.freeze({
  // Read
  VIEW:                'view',

  // Communication
  CALL_LOGGED:         'call',
  SMS_SENT:            'sms_sent',
  SMS_RECEIVED:        'sms_received',
  EMAIL_SENT:          'email_sent',

  // Consent (Priority 1)
  CONSENT_SENT:        'consent_sent',
  CONSENT_SIGNED:      'consent_signed',
  CONSENT_DECLINED:    'consent_declined',
  CONSENT_REVOKED:     'consent_revoked',
  CONSENT_EXPIRED:     'consent_expired',

  // Policy
  POLICY_CREATED:      'policy_created',
  POLICY_UPDATED:      'policy_updated',
  POLICY_DELETED:      'policy_deleted',
  POLICY_RENEWED:      'policy_renewed',
  POLICY_CANCELLED:    'policy_cancelled',

  // Pipeline
  LEAD_STATUS:         'lead_status',
  LEAD_CREATED:        'lead_created',
  LEAD_CONVERTED:      'lead_converted',

  // Automation (Priority 3)
  WORKFLOW_ENROLL:     'workflow_enroll',
  WORKFLOW_STEP:       'workflow_step',
  WORKFLOW_COMPLETE:   'workflow_complete',
  WORKFLOW_EXIT:       'workflow_exit',

  // Notes / files
  NOTE_ADDED:          'note_added',
  FILE_UPLOADED:       'file_uploaded',
  TAG_ADDED:           'tag_added',
  TAG_REMOVED:         'tag_removed',
});

const VALID_ACTIONS = new Set(Object.values(AUDIT_ACTION));

// Sanitize a metadata object — drop keys whose name suggests a secret.
const SECRET_KEY_PATTERN = /password|token|secret|api[_-]?key|service[_-]?role/i;
function sanitizeMetadata(meta) {
  if (!meta || typeof meta !== 'object') return {};
  const out = {};
  for (const [k, v] of Object.entries(meta)) {
    if (SECRET_KEY_PATTERN.test(k)) continue;
    out[k] = v;
  }
  return out;
}

// Best-effort browser UA capture. Returns the truthy string or null;
// SSR contexts (or stripped UAs) yield null and we just skip the
// column rather than blocking the write.
function captureUserAgent() {
  try {
    if (typeof navigator !== 'undefined' && navigator.userAgent) {
      return navigator.userAgent;
    }
  } catch {}
  return null;
}

// Single entry point. Returns the promise (so callers can `await` if
// they want — most should not).
export function audit(opts = {}) {
  const {
    ownerId,
    actorId      = null,
    actorName    = null,
    targetType,
    targetId     = null,
    targetLabel  = null,
    action,
    metadata     = {},
    ipAddress    = null, // server-side capture writes this; browser leaves null
  } = opts;

  if (!ownerId)     return Promise.resolve(null);
  if (!targetType)  return Promise.resolve(null);
  if (!action)      return Promise.resolve(null);
  if (!VALID_ACTIONS.has(action)) {
    // Typo guard — surface in console so it gets caught in dev.
    console.warn(`[audit] unknown action "${action}"; refusing write.`);
    return Promise.resolve(null);
  }

  const row = {
    owner_id:     ownerId,
    actor_id:     actorId,
    actor_name:   actorName,
    target_type:  targetType,
    target_id:    targetId,
    target_label: targetLabel,
    action,
    metadata:     sanitizeMetadata(metadata),
    ip_address:   ipAddress,
    user_agent:   captureUserAgent(),
  };

  return supabase.from('audit_logs').insert(row).then(({ error }) => {
    if (error) console.warn('[audit] write failed:', error.message);
    return error ? null : true;
  });
}

// Read helper — fetches the timeline for a specific record.
// Used by AuditLogViewer in the client/lead drawer.
export async function fetchAuditTimeline({ targetType, targetId, limit = 100 }) {
  if (!targetType || !targetId) return [];
  const { data, error } = await supabase
    .from('audit_logs')
    .select('*')
    .eq('target_type', targetType)
    .eq('target_id', targetId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) {
    console.warn('[audit] timeline read failed:', error.message);
    return [];
  }
  return data || [];
}

// Owner-wide recent activity (for the Health Portal "Recent Activity"
// card and the agency-wide audit feed).
export async function fetchOwnerActivity({ limit = 50 } = {}) {
  const { data, error } = await supabase
    .from('audit_logs')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) return [];
  return data || [];
}
